import { Input, Modal } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAnalytics } from 'features/analytics'
import { useGeckoAuth } from 'features/auth'
import { createChatApi } from 'features/chatbot/api/chatApi'
import { connectToStream } from 'features/chatbot/api/sseClient'
import type { ChatStreamEvent } from 'features/chatbot/types/stream'
import { PermissionsGate } from 'features/permissions'
import {
  Brain,
  ChevronDown,
  ChevronRight,
  FileText,
  MessageCircle,
  Send,
  Sparkles,
  X,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'
import { BaseSensorEntry, PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

// Shared styled components
const DrawerBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 999;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: #0a0a0a;
  border-left: 1px solid #333;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(102, 126, 234, 0.03) 0%,
      rgba(118, 75, 162, 0.02) 50%,
      rgba(0, 0, 0, 0.1) 100%
    );
    pointer-events: none;
  }
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-bottom: 1px solid #333;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  }
`

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 11px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: 0.8;
  }

  svg {
    width: 20px;
    height: 20px;
    color: white;
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  }
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2px;
`

const HeaderSubtitle = styled.div`
  font-size: 13px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: #4ade80;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
`

const HeaderControls = styled.div`
  display: flex;
  gap: 4px;
`

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid #333;
  border-radius: 8px;
  cursor: pointer;
  color: #888;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: #2a2a2a;
    border-color: #555;
    color: #fff;
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const DrawerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ChatContent = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const WelcomeMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
  gap: 20px;
  padding: 40px 20px;
  color: #888;
`

const WelcomeIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    border-radius: 18px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: 0.8;
  }

  svg {
    width: 40px;
    height: 40px;
    color: white;
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
`

const WelcomeTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 12px;
`

const WelcomeText = styled.div`
  font-size: 15px;
  line-height: 1.6;
  max-width: 320px;
  color: #aaa;
`

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  margin-top: 24px;
`

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  font-size: 14px;
  color: #ccc;

  [data-feature-icon='true'] {
    width: 20px;
    height: 20px;
    color: #667eea;
    flex-shrink: 0;
  }
`

const ContextInfo = styled.div`
  background: ${({ theme }) => theme.forecast?.background2 || '#1a1a1a'};
  border: 1px solid ${({ theme }) => theme.forecast?.background3 || '#333'};
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.forecast?.defaultText || '#888'};
`

const ContextLabel = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast?.activeText || '#fff'};
  margin-bottom: 4px;
`

const MessagesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding: 16px 0;
  min-height: 0;
`

const Message = styled.div<{ $isUser: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  ${({ $isUser }) =>
    $isUser
      ? 'flex-direction: row-reverse; float: right'
      : 'flex-direction: column;'}
`

const MessageAvatar = styled.div<{ $isUser: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $isUser }) =>
    $isUser ? '#2a2a2a' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  font-size: 12px;
  font-weight: 600;

  svg {
    width: 16px;
    height: 16px;
  }
`

const MessageBubble = styled.div<{ $isUser: boolean }>`
  background: ${({ $isUser }) => ($isUser ? '#2a2a2a' : '#1a1a1a')};
  border: 1px solid ${({ $isUser }) => ($isUser ? '#444' : '#333')};
  border-radius: 12px;
  padding: 12px 16px;
  max-width: 100%;
  color: #fff;
  font-size: 14px;
  line-height: 1.5;

  ${({ $isUser }) =>
    $isUser &&
    `
    border-bottom-right-radius: 4px;
  `}
  ${({ $isUser }) =>
    !$isUser &&
    `
    border-bottom-left-radius: 4px;
  `}
  p {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  code {
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 13px;
  }
`

const ChatInputContainer = styled.div`
  display: flex;
  gap: 8px;
  padding: 20px 24px;
  border-top: 1px solid #333;
  background: #0a0a0a;
  align-items: flex-end;
`

const ChatInputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const SendButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ $disabled }) =>
    $disabled ? '#333' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border: none;
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $disabled }) => ($disabled ? '#666' : 'white')};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      `
      transform: translateY(-1px);
      box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
    `}
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const ThoughtsContainer = styled.div`
  background: rgba(102, 126, 234, 0.05);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 8px;
  padding: 8px 12px;
  margin: 8px 0;
  width: 100%;
`

const ThoughtsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #888;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #aaa;
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s;
  }
`

const ThoughtsContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${({ $isExpanded }) => ($isExpanded ? '200px' : '0')};
  overflow: auto;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ul {
    margin: 8px 0 0 0;
    padding-left: 16px;
    list-style: none;

    li {
      font-size: 13px;
      color: #999;
      margin: 4px 0;
      padding-left: 16px;
      position: relative;

      &::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #667eea;
      }
    }
  }
`

const SourcesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 0;
  border-top: 1px solid #333;
`

const SourceCard = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #ccc;

  &:hover {
    background: #2a2a2a;
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
  }

  svg {
    width: 14px;
    height: 14px;
    color: #667eea;
  }
`

const MetadataModal = styled(Modal)`
  .ant-modal-content {
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 12px;
  }

  .ant-modal-header {
    background: #1a1a1a;
    border-bottom: 1px solid #333;
    border-radius: 12px 12px 0 0;
  }

  .ant-modal-title {
    color: #fff;
    font-weight: 600;
  }

  .ant-modal-close {
    color: #888;

    &:hover {
      color: #fff;
    }
  }

  .ant-modal-body {
    padding: 20px;
  }
`

const MetadataContent = styled.pre`
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 16px;
  overflow: auto;
  max-height: 400px;
  color: #ccc;
  font-size: 13px;
  font-family: 'Monaco', 'Consolas', monospace;
  margin: 0;
`

interface ChatMessage {
  id: string
  text: string
  isUser: boolean
  timestamp: Date
  thoughts?: string[]
  sources?: Source[]
}

interface Source {
  id: string
  text: string
  data: Record<string, unknown>
}

export interface CantileverAIContext {
  type: 'asset' | 'system'
  name: string // unit name or system name
  entryId: string // unit ID or system ID
  siteId?: string
  sensors?: BaseSensorEntry[] | null
  sensorTags?: BaseSensorEntry[] | null
  organizationId?: string
  pageContext?: {
    currentSystem?: {
      id: string
      name: string
      components: Array<{ status: string }>
    }
    selectedUnit?: {
      id: string
      name: string
      status: string
    }
  }
}

interface CantileverAIAssistantProps {
  className?: string
  isOpen?: boolean
  onClose?: () => void
  context: CantileverAIContext
}

export const CantileverAIAssistant: React.FC<CantileverAIAssistantProps> = ({
  className,
  isOpen = false,
  onClose,
  context,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [expandedThoughts, setExpandedThoughts] = useState<Set<string>>(
    new Set()
  )
  const [selectedSource, setSelectedSource] = useState<Source | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [streamingThoughts, setStreamingThoughts] = useState<string[]>([])
  const [streamingSources, setStreamingSources] = useState<Source[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { trackButtonClicked, trackEvent } = useGeckoAnalytics()
  const { authorization } = useGeckoAuth()
  const { LLM_SERVICE } = useRuntimeVariables()

  const api = useMemo(
    () => createChatApi(LLM_SERVICE, authorization ?? ''),
    [LLM_SERVICE, authorization]
  )

  const thoughtsAccRef = useRef<string[]>([])
  const sourcesAccRef = useRef<Source[]>([])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, streamingThoughts, streamingSources])

  useEffect(() => {
    // Add welcome message when first opened if no messages exist
    if (isOpen && messages.length === 0) {
      // Track session start
      trackEvent('Cantilever AI Session Started', {
        ...context,
        has_context: String(!!context.sensors && context.sensors.length > 0),
      })

      // Reset session ID for new session
      setSessionId(undefined)

      const welcomeMessage: ChatMessage = {
        id: Date.now().toString(),
        text: "Hello! I'm Cantilever AI, your intelligent system analysis assistant. I have access to your equipment's OEM manuals, real-time sensor data, and maintenance history. How can I help you understand your asset today?",
        isUser: false,
        timestamp: new Date(),
      }
      setMessages([welcomeMessage])
    }
  }, [isOpen, messages.length, trackEvent, context])

  const toggleThoughts = (messageId: string) => {
    setExpandedThoughts((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(messageId)) {
        newSet.delete(messageId)
      } else {
        newSet.add(messageId)
      }
      return newSet
    })
  }

  const handleSourceClick = (source: Source) => {
    setSelectedSource(source)
    setIsModalOpen(true)

    trackEvent('Cantilever AI Source Clicked', {
      ...context,
      source_id: source.id,
      source_type: source.id.startsWith('entry-') ? 'entry' : 'document',
    })
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedSource(null)
  }

  const handleClose = () => {
    // Track chat session end with full conversation history
    if (messages.length > 0) {
      trackEvent('Cantilever AI Session Ended', {
        ...context,
        session_id: sessionId,
        session_duration:
          Date.now() - new Date(messages[0].timestamp).getTime(),
        total_messages: messages.length,
        user_messages: messages.filter((m) => m.isUser).length,
        ai_messages: messages.filter((m) => !m.isUser).length,
        conversation_history: messages.map((m) => ({
          is_user: m.isUser,
          text: m.text,
          timestamp: m.timestamp.toISOString(),
          message_length: m.text.length,
        })),
      })
    }

    trackButtonClicked({
      button_name: 'Close Cantilever AI',
      ...context,
    })

    setSessionId(undefined)
    setMessages([])
    setExpandedThoughts(new Set())
    setSelectedSource(null)
    setIsModalOpen(false)
    setStreamingThoughts([])
    setStreamingSources([])

    if (onClose) {
      onClose()
    }
    setIsExpanded(false)
  }

  const handleStreamEvent = useCallback(
    (event: ChatStreamEvent) => {
      switch (event.type) {
        case 'thinking': {
          thoughtsAccRef.current = [...thoughtsAccRef.current, event.text]
          setStreamingThoughts([...thoughtsAccRef.current])
          break
        }
        case 'tool_call': {
          const text = `Calling ${event.name}`
          thoughtsAccRef.current = [...thoughtsAccRef.current, text]
          setStreamingThoughts([...thoughtsAccRef.current])
          break
        }
        case 'tool_result': {
          const refs = event.references
          const newSources: Source[] = [
            ...refs.documents.map((doc) => ({
              id: doc.id,
              text: doc.title || doc.id,
              data: { ...doc } as Record<string, unknown>,
            })),
            ...refs.instruments.map((inst) => ({
              id: `inst-${inst.id}`,
              text: inst.instrumentName,
              data: { ...inst } as Record<string, unknown>,
            })),
          ]
          if (newSources.length > 0) {
            sourcesAccRef.current = [...sourcesAccRef.current, ...newSources]
            setStreamingSources([...sourcesAccRef.current])
          }
          break
        }
        case 'done': {
          const aiResponse: ChatMessage = {
            id: Date.now().toString(),
            text: event.content,
            isUser: false,
            timestamp: new Date(),
            thoughts:
              thoughtsAccRef.current.length > 0
                ? [...thoughtsAccRef.current]
                : undefined,
            sources:
              sourcesAccRef.current.length > 0
                ? [...sourcesAccRef.current]
                : undefined,
          }
          setMessages((prev) => [...prev, aiResponse])
          setIsLoading(false)
          setStreamingThoughts([])
          setStreamingSources([])

          trackEvent('Cantilever AI Response Received', {
            ...context,
            response_length: event.content.length,
            has_context: String(
              !!context.sensors && context.sensors.length > 0
            ),
            success: 'true',
            response_text: event.content,
            session_id: sessionId,
            has_thoughts: String(thoughtsAccRef.current.length > 0),
            has_sources: String(sourcesAccRef.current.length > 0),
          })

          thoughtsAccRef.current = []
          sourcesAccRef.current = []
          break
        }
        case 'error': {
          const errorResponse: ChatMessage = {
            id: (Date.now() + 1).toString(),
            text: event.error,
            isUser: false,
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, errorResponse])
          setIsLoading(false)
          setStreamingThoughts([])
          setStreamingSources([])
          thoughtsAccRef.current = []
          sourcesAccRef.current = []

          trackEvent('Cantilever AI Response Error', {
            ...context,
            has_context: String(
              !!context.sensors && context.sensors.length > 0
            ),
            error_message: event.error,
          })
          break
        }
        default:
          break
      }
    },
    [context, sessionId, trackEvent]
  )

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputValue.trim(),
      isUser: true,
      timestamp: new Date(),
    }

    trackEvent('Cantilever AI Message Sent', {
      context,
      session_id: sessionId,
      message_length: inputValue.trim().length,
      has_context: String(!!context.sensors && context.sensors.length > 0),
      message_text: inputValue.trim(),
    })

    setMessages((prev) => [...prev, userMessage])
    const messageText = inputValue.trim()
    setInputValue('')
    setIsLoading(true)
    setStreamingThoughts([])
    setStreamingSources([])
    thoughtsAccRef.current = []
    sourcesAccRef.current = []

    let systemContext = `You are Cantilever AI, an AI assistant specialized in asset monitoring and analysis.
      Be succinct when answering, quote data sources (file name, section) when possible.
      Use markdown to format your responses.`
    if (context.sensors && context.sensors.length > 0) {
      const sensorContext = context.sensors.map((sensor) => sensor.data)
      systemContext += '\n\nSensor Data:\n' + JSON.stringify(sensorContext)
    }
    const enrichedContent = `${systemContext}\n\nThe user has asked you a question: ${messageText}\nHere is some automatically extracted page context: ${JSON.stringify(context)}`

    try {
      let activeSessionId = sessionId
      if (!activeSessionId) {
        const sessionContext: Record<string, string> = {
          org_id: context.organizationId || '',
          site_id: context.siteId || '',
        }
        if (context.entryId) {
          sessionContext[
            context.type === 'asset' ? 'unit_id' : 'component_id'
          ] = context.entryId
        }
        const response = await api.createSession({
          mode: 'site_analysis',
          context: sessionContext,
        })
        activeSessionId = response.session_id
        setSessionId(activeSessionId)
      }

      connectToStream(
        api,
        activeSessionId,
        { content: enrichedContent, include_tool_logs: true },
        {
          onEvent: handleStreamEvent,
          onError: (error) => {
            const errorResponse: ChatMessage = {
              id: (Date.now() + 1).toString(),
              text: error,
              isUser: false,
              timestamp: new Date(),
            }
            setMessages((prev) => [...prev, errorResponse])
            setIsLoading(false)
            setStreamingThoughts([])
            setStreamingSources([])
            thoughtsAccRef.current = []
            sourcesAccRef.current = []
          },
          onComplete: () => {},
        }
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send message'
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: errorMessage,
        isUser: false,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorResponse])
      setIsLoading(false)
    }
  }, [
    inputValue,
    isLoading,
    context,
    trackEvent,
    sessionId,
    api,
    handleStreamEvent,
  ])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const getSubtitleText = () => {
    return context.name ? `Analyzing ${context.name}` : 'Ready to assist'
  }

  const getContextInfo = () => {
    return (
      <ContextInfo>
        <ContextLabel>Current Context: {context.name}</ContextLabel>
        <div>Sensors: {context.sensorTags?.length || 0} active</div>
        {context.pageContext?.selectedUnit && (
          <div>
            Selected: {context.pageContext.selectedUnit.name} (
            {context.pageContext.selectedUnit.status})
          </div>
        )}
      </ContextInfo>
    )
  }

  return (
    <div className={className}>
      <DrawerBackdrop
        $isOpen={isOpen}
        onClick={handleClose}
        style={{
          background: isExpanded ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.6)',
        }}
      />

      <DrawerContainer
        $isOpen={isOpen}
        style={{
          width: isExpanded ? '96vw' : 400,
          height: isExpanded ? '90vh' : '100vh',
          maxWidth: '100vw',
          borderRadius: isExpanded ? 12 : 0,
          border: '1px solid #333',
          boxShadow: isExpanded
            ? '0 12px 48px rgba(0,0,0,0.6)'
            : '-8px 0 32px rgba(0,0,0,0.5)',
          transform: !isOpen
            ? 'translateX(100%)'
            : isExpanded
              ? 'translateX(-2vw) translateY(5vh)'
              : 'translateX(0)',
          transition:
            'width 300ms cubic-bezier(0.4, 0, 0.2, 1), height 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
        <DrawerHeader>
          <HeaderInfo>
            <HeaderIcon>
              <Brain />
            </HeaderIcon>
            <HeaderText>
              <HeaderTitle>Cantilever AI</HeaderTitle>
              <HeaderSubtitle>
                <Sparkles style={{ width: 12, height: 12 }} />
                {getSubtitleText()}
              </HeaderSubtitle>
            </HeaderText>
          </HeaderInfo>
          <HeaderControls>
            <HeaderButton
              onClick={() => setIsExpanded((v) => !v)}
              title={isExpanded ? 'Collapse' : 'Expand'}>
              <span style={{ fontSize: 12 }}>{isExpanded ? '⤢' : '⤢'}</span>
            </HeaderButton>
            <HeaderButton onClick={handleClose} title='Close'>
              <X />
            </HeaderButton>
          </HeaderControls>
        </DrawerHeader>

        <DrawerContent>
          <ChatContent>
            {messages.length === 0 ? (
              <WelcomeMessage>
                <WelcomeIcon>
                  <Brain />
                </WelcomeIcon>
                <div>
                  <WelcomeTitle>Welcome to Cantilever AI</WelcomeTitle>
                  <WelcomeText>
                    Your intelligent asset analysis assistant. I can help you
                    understand equipment performance, diagnose issues, and
                    provide maintenance recommendations.
                  </WelcomeText>
                  <FeatureList>
                    <FeatureItem>
                      <MessageCircle data-feature-icon='true' />
                      <span>OEM manual guidance & troubleshooting</span>
                    </FeatureItem>
                    <FeatureItem>
                      <Sparkles data-feature-icon='true' />
                      <span>Real-time sensor data analysis</span>
                    </FeatureItem>
                  </FeatureList>
                </div>
              </WelcomeMessage>
            ) : (
              <>
                {getContextInfo()}

                <MessagesContainer>
                  {messages.map((message) => (
                    <React.Fragment key={message.id}>
                      <PermissionsGate
                        requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
                        {!message.isUser &&
                          message.thoughts &&
                          message.thoughts.length > 0 && (
                            <ThoughtsContainer>
                              <ThoughtsHeader
                                onClick={() => toggleThoughts(message.id)}>
                                {expandedThoughts.has(message.id) ? (
                                  <ChevronDown />
                                ) : (
                                  <ChevronRight />
                                )}
                                <span>
                                  AI Thoughts ({message.thoughts.length})
                                </span>
                              </ThoughtsHeader>
                              <ThoughtsContent
                                $isExpanded={expandedThoughts.has(message.id)}>
                                <ul>
                                  {message.thoughts.map(
                                    (thought, thoughtIndex) => (
                                      <li
                                        key={`${message.id}-thought-${thoughtIndex}`}>
                                        {thought}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </ThoughtsContent>
                            </ThoughtsContainer>
                          )}
                      </PermissionsGate>

                      {/* Message */}
                      <Message $isUser={message.isUser}>
                        <MessageAvatar $isUser={message.isUser}>
                          {message.isUser ? 'U' : <Brain />}
                        </MessageAvatar>
                        <MessageBubble $isUser={message.isUser}>
                          <ReactMarkdown>{message.text}</ReactMarkdown>
                        </MessageBubble>
                      </Message>

                      {/* Show sources after AI message */}
                      {!message.isUser &&
                        message.sources &&
                        message.sources.length > 0 && (
                          <SourcesContainer>
                            {message.sources.map((source) => (
                              <SourceCard
                                key={source.id}
                                onClick={() => handleSourceClick(source)}>
                                <FileText />
                                <span>{source.text}</span>
                              </SourceCard>
                            ))}
                          </SourcesContainer>
                        )}
                    </React.Fragment>
                  ))}
                  {isLoading && streamingThoughts.length > 0 && (
                    <ThoughtsContainer>
                      <ThoughtsHeader>
                        <Brain />
                        <span>AI is thinking...</span>
                      </ThoughtsHeader>
                      <ThoughtsContent $isExpanded={true}>
                        <ul>
                          {streamingThoughts.map((thought, index) => (
                            <li key={`streaming-thought-${index}`}>
                              {thought}
                            </li>
                          ))}
                        </ul>
                      </ThoughtsContent>
                    </ThoughtsContainer>
                  )}
                  {isLoading && (
                    <Message $isUser={false}>
                      <MessageAvatar $isUser={false}>
                        <Brain />
                      </MessageAvatar>
                      <MessageBubble $isUser={false}>Thinking...</MessageBubble>
                    </Message>
                  )}
                  <div ref={messagesEndRef} />
                </MessagesContainer>
              </>
            )}
          </ChatContent>

          <ChatInputContainer>
            <ChatInputWrapper>
              <Input.TextArea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Ask about your ${context.type}, sensors, or maintenance...`}
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={isLoading}
              />
            </ChatInputWrapper>
            <SendButton
              onClick={handleSendMessage}
              $disabled={!inputValue.trim() || isLoading}
              title='Send message'>
              <Send />
            </SendButton>
          </ChatInputContainer>
        </DrawerContent>
      </DrawerContainer>

      {/* Metadata Modal */}
      <MetadataModal
        title={selectedSource?.text || 'Source Details'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={600}>
        {selectedSource && (
          <MetadataContent>
            {JSON.stringify(selectedSource.data, null, 2)}
          </MetadataContent>
        )}
      </MetadataModal>
    </div>
  )
}

export default CantileverAIAssistant
